import { Fragment, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, PackageDetail, Engagement, Bid, BidLineItem, rmb, finalPriceLabel, comparisonExportUrl, openBids, startPortalPreview } from '../lib/api';
import { useResizableColumns } from '../lib/useResizableColumns';
import { NegotiationPanel } from './NegotiationPanel';
import { CompanyHistoryPanel } from './CompanyHistoryPanel';
import CreateTaskHere from './CreateTaskHere';

const CLEAN_PILL: Record<string, { cls: string; zh: string }> = {
  clean: { cls: 'pill-good', zh: '清标通过' },
  has_outliers: { cls: 'pill-warn', zh: '有单项偏离' },
  high_outlier: { cls: 'pill-bad', zh: '总价偏高' },
  low_outlier: { cls: 'pill-warn', zh: '异常低价' },
  missing_items: { cls: 'pill-bad', zh: '缺项' },
  pending: { cls: 'pill-muted', zh: '待清标' },
};

// 多轮清单版本管理: an archived round is a prior 清单 version kept for history (excluded from active views).
const isArchived = (b: Bid): boolean => !!(b as { archived?: number }).archived;

function latestRound(e: Engagement): number {
  return e.bids.filter((b) => !isArchived(b)).reduce((m, b) => Math.max(m, b.round_number), 1);
}

// fb_3fb35bdc · a comparison column is one (单价 | 合计) pair. In 比家 mode each column is a labor at
// one round; in 比轮 mode each column is one round of a single labor — so the same render path serves both.
type ViewCol = { key: string; e: Engagement; round: number; bid: Bid | undefined; label: string };

// voteBlocked · 定标投票 §6: while the panel still owes votes the server 409s POST …/award, so the
// button is shut here too rather than firing a doomed request. Opt-in — undefined/false on every
// package that never voted.
export function ComparisonTable({ pkg, voteBlocked = false }: { pkg: PackageDetail; voteBlocked?: boolean }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['package', pkg.id] });

  // 比家/比轮 operate on ACTIVE bids (engagements with a live round); 比家比轮 additionally surfaces archived
  // history. colsAll keeps engagements that now have only archived rounds so their history still shows.
  const activeBids = (e: Engagement): Bid[] => e.bids.filter((b) => !isArchived(b));
  const cols = pkg.engagements.filter((e) => !e.withdrawn && activeBids(e).length > 0);
  const colsAll = pkg.engagements.filter((e) => !e.withdrawn && e.bids.length > 0);
  const [rounds, setRounds] = useState<Record<string, number>>({});
  const [negEng, setNegEng] = useState<Engagement | null>(null);
  const roundFor = (e: Engagement) => rounds[e.id] ?? latestRound(e);
  const bidFor = (e: Engagement): Bid | undefined => activeBids(e).find((b) => b.round_number === roundFor(e));

  // fb_3fb35bdc (改改): 比家 = compare labors (each at one round); 比轮 = one labor's rounds side by side;
  // 比家比轮 = every labor × every round — incl. archived rounds from prior 清单 versions (改改 7/1 history).
  const [viewMode, setViewMode] = useState<'byLabor' | 'byRound' | 'allRounds'>('byLabor');
  const [roundLaborId, setRoundLaborId] = useState<string | null>(null);
  const multiRound = colsAll.some((e) => e.bids.length > 1);
  const hasHistory = multiRound || colsAll.some((e) => e.bids.some((b) => isArchived(b)));   // F11
  const roundLabor = cols.find((e) => e.id === roundLaborId) ?? cols.find((e) => activeBids(e).length > 1) ?? cols[0] ?? null;
  const inRoundMode = viewMode === 'byRound' && !!roundLabor;
  const inAllMode = viewMode === 'allRounds';
  const viewCols: ViewCol[] = inAllMode
    ? colsAll.flatMap((e) => [...e.bids].sort((a, b) => a.round_number - b.round_number)
        .map((b) => ({ key: `${e.id}_R${b.round_number}`, e, round: b.round_number, bid: b,
          label: `${e.labor_co_name ?? '—'} 第${b.round_number}轮${isArchived(b)
            ? (b.is_final_offer ? '·已撤回最终价' : `·旧清单v${(b as { archived_boq_revision?: number | null }).archived_boq_revision ?? '?'}`)
            : (b.is_final_offer ? '·最终报价' : '')}` })))
    : inRoundMode
    ? activeBids(roundLabor!).sort((a, b) => a.round_number - b.round_number)
        .map((b) => ({ key: `${roundLabor!.id}_R${b.round_number}`, e: roundLabor!, round: b.round_number, bid: b, label: `第 ${b.round_number} 轮 / R${b.round_number}` }))
    : cols.map((e) => ({ key: e.id, e, round: roundFor(e), bid: bidFor(e), label: e.labor_co_name ?? '—' }));
  // per-labor win/lose tinting applies in 比家 & 比家比轮 (each column maps to a labor); suppressed only in 比轮.
  const colClass = (c: ViewCol) => (inRoundMode ? '' : awardClass(c.e));

  // 改改 6/26: drag-resizable columns + horizontal scroll so the dense grid (esp. 项目特征) reads cleanly.
  const COL_DEF: Record<string, number> = { seq: 48, name: 150, spec: 240, unit: 56, qty: 72, ref: 90 };
  const rz = useResizableColumns((id) => COL_DEF[id] ?? (id.startsWith('up_') ? 96 : id.startsWith('amt_') ? 108 : 100));
  const colIds = ['seq', 'name', 'spec', 'unit', 'qty', 'ref', ...viewCols.flatMap((c) => [`up_${c.key}`, `amt_${c.key}`])];

  const envelope = pkg.budget_envelope_rmb;
  const awarded = !!pkg.awarded_engagement_id;

  const shortlist = useMutation({
    mutationFn: (v: { eid: string; on: boolean }) => api(`/packages/${pkg.id}/engagements/${v.eid}/shortlist`, { method: 'POST', body: JSON.stringify({ shortlisted: v.on }) }),
    onSuccess: invalidate,
  });
  const award = useMutation({
    mutationFn: (eid: string) => api(`/packages/${pkg.id}/engagements/${eid}/award`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: invalidate,
  });
  const unaward = useMutation({
    mutationFn: () => api(`/packages/${pkg.id}/unaward`, { method: 'POST', body: '{}' }),
    onSuccess: invalidate,
  });
  const openBidsMut = useMutation({
    mutationFn: () => openBids(pkg.id),
    onSuccess: invalidate,
  });

  // B7/A7 · while sealed (a deadline is set and 开标 hasn't happened) the rival prices stay hidden.
  if (pkg.sealed) {
    const dueMs = pkg.tender_due_at ? Date.parse(pkg.tender_due_at) : null;
    const reached = dueMs == null || Date.now() >= dueMs;
    const openErr = openBidsMut.isError ? ((openBidsMut.error as any)?.body?.error ?? '开标失败 / open failed') : null;
    return (
      <div className="card" style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 18, marginBottom: 6 }}>🔒 报价已密封 / Bids sealed</div>
        <div className="muted small" style={{ marginBottom: 14 }}>
          {pkg.tender_due_at
            ? <>截标时间 / Deadline: <strong>{new Date(pkg.tender_due_at).toLocaleString('zh-CN')}</strong> — 截标后由招标人开标，方可查看比价。 / Bids open after the deadline.</>
            : '尚未开标。 / Not opened yet.'}
        </div>
        <button
          className="btn-primary"
          disabled={!reached || openBidsMut.isPending}
          title={reached ? '' : '未到截标时间 / before the deadline'}
          onClick={() => { if (confirm('确认开标？开标后报价对所有评标人可见且不可逆。 / Open bids? This reveals all bids and cannot be undone.')) openBidsMut.mutate(); }}
        >
          {openBidsMut.isPending ? '开标中… / Opening…' : '🔓 开标 / Open bids'}
        </button>
        {!reached && <div className="muted small" style={{ marginTop: 8 }}>未到截标时间，暂不能开标 / cannot open before the deadline</div>}
        {openErr && <div className="small" style={{ color: '#c0392b', marginTop: 8 }}>{openErr}</div>}
      </div>
    );
  }

  if (colsAll.length === 0) {
    return <div className="empty">暂无报价可对比 — 先邀请劳务方并录入报价。 / No bids to compare yet.</div>;
  }

  // Match a bid's line to the CURRENT 清单 row: live rounds align by work_item_id; an archived (旧清单) round's
  // work_item was replaced, so fall back to the snapshot name. Assign archived lines ONCE, in 清单 order, so a
  // duplicate 名称 never smears one archived price across multiple rows. Renamed/removed old items match
  // nothing here (their amount still counts in that column's frozen 合计).
  const archivedLineByRow = new Map<string, Map<string, BidLineItem>>(); // bid.id → work_item.id → line
  for (const c of viewCols) {
    const b = c.bid;
    if (!b || !isArchived(b) || archivedLineByRow.has(b.id)) continue;
    const remaining = b.line_items.filter((l) => l.snapshot_item_label != null);
    const m = new Map<string, BidLineItem>();
    for (const w of pkg.work_items) {
      const idx = remaining.findIndex((l) => l.snapshot_item_label === w.item_label);
      if (idx >= 0) { m.set(w.id, remaining[idx]); remaining.splice(idx, 1); }
    }
    // 7/2 walkthrough F8: a round archived WITHOUT a 清单 replacement (e.g. a withdrawn 最终报价 — reopen
    // archives it while the work_items live on) carries no snapshots; align those lines by work_item_id
    // like a live round, else the whole column renders "—" with only its total showing.
    for (const l of b.line_items) {
      if (l.snapshot_item_label == null && l.work_item_id != null && !m.has(l.work_item_id)) m.set(l.work_item_id, l);
    }
    archivedLineByRow.set(b.id, m);
  }
  const lineOf = (bid: Bid | undefined, w: { id: string; item_label: string }) =>
    bid ? (isArchived(bid) ? archivedLineByRow.get(bid.id)?.get(w.id) : bid.line_items.find((l) => l.work_item_id === w.id)) : undefined;

  // 改改 6/26 (fb_27131366): per column show 不含税单价 + 不含税合计, color-marked RELATIVE to the other columns
  // on the same line — highest 单价 = 偏高 (red), lowest = 偏低 (green). hi/lo are this line's extremes across
  // the shown columns (other labors in 比家 mode, or the labor's other rounds in 比轮 mode).
  function bidderCells(c: ViewCol, w: { id: string; item_label: string }, hi: number | null, lo: number | null) {
    const li = lineOf(c.bid, w);
    const ac = colClass(c);
    const p = li?.unit_price_rmb ?? null;
    const cls = p != null && p === hi ? 'out-high' : p != null && p === lo ? 'out-low' : 'within';
    return (
      <Fragment key={c.key}>
        <td className={`num ${ac}`}>{p != null ? <span className={cls}>{rmb(p)}</span> : '—'}</td>
        <td className={`num ${ac}`}>{li?.amount_rmb != null ? <span className={cls}>{rmb(li.amount_rmb)}</span> : '—'}</td>
      </Fragment>
    );
  }
  function awardClass(e: Engagement) {
    if (e.award_status === 'awarded') return 'win-col';
    if (awarded && e.award_status === 'lost') return 'lose-col';
    return '';
  }
  // 7/2 walkthrough F5: the negotiation lock state was invisible outside the 议价 panel — yet it's the
  // decision-critical fact when 改改 scans the compare view to award. Surface it as a pill.
  // fb_e5f31dc1 (改改): the negotiated figure reads 不含税 · 税率 · 含税 when a 税金点 was set.
  function finalPill(e: Engagement) {
    if (e.final_confirmed_at) return <span className="pill pill-good" title={`劳务已确认，价格已锁定 / confirmed & locked`}>🔒 最终价已锁定 {finalPriceLabel(e.final_total_rmb, e.final_tax_pct)}</span>;
    if (e.final_confirm_requested_at) return <span className="pill pill-warn" title="最终报价已发出，待劳务确认 / awaiting labor confirmation">⏳ 最终价待确认 {finalPriceLabel(e.final_total_rmb, e.final_tax_pct)}</span>;
    return null;
  }

  return (
    <div>
      <div className="row" style={{ marginBottom: 8, alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="small muted">共 {cols.length} 家报价 · {pkg.work_items.length} 项清单 · 拖动表头右缘可调整列宽 / drag column edges to resize</span>
        {/* fb_3fb35bdc · 比家 / 比轮 toggle */}
        <div className="row" style={{ gap: 4 }}>
          <button type="button" className={viewMode === 'byLabor' ? 'btn-primary' : ''} style={{ padding: '3px 10px' }} onClick={() => setViewMode('byLabor')}>比家 / By labor</button>
          <button type="button" className={viewMode === 'byRound' ? 'btn-primary' : ''} style={{ padding: '3px 10px' }} disabled={!multiRound} title={multiRound ? '同一家劳务各轮报价并排对比' : '需要某家劳务有两轮以上报价 / needs a labor with ≥2 rounds'} onClick={() => setViewMode('byRound')}>比轮 / By round</button>
          {/* 7/2 walkthrough F11: right after a clear-bids re-import every round is ARCHIVED (1 per labor) —
              the history view must stay reachable then, exactly when 改改 checks "did my old rounds survive?" */}
          <button type="button" className={viewMode === 'allRounds' ? 'btn-primary' : ''} style={{ padding: '3px 10px' }} disabled={!hasHistory} title={hasHistory ? '所有劳务×所有轮次同表对比，含已存档旧轮（劳务1第一轮/第二轮·劳务2第一轮/第二轮…）' : '需要多轮报价或已存档轮次 / needs multiple rounds or archived history'} onClick={() => setViewMode('allRounds')}>比家比轮 / All</button>
          {inRoundMode && (
            <select value={roundLabor!.id} onChange={(ev) => setRoundLaborId(ev.target.value)} style={{ width: 'auto', padding: '2px 6px', fontSize: 12 }}>
              {cols.map((e) => <option key={e.id} value={e.id}>{e.labor_co_name}</option>)}
            </select>
          )}
        </div>
        <span className="spacer" style={{ flex: 1 }} />
        {rz.resized && <button type="button" onClick={rz.reset}>↺ 重置列宽 / Reset</button>}
        <button type="button" onClick={() => window.open(comparisonExportUrl(pkg.id), '_blank')}>⬇ 导出对比表 / Export</button>
      </div>
      <div className="cmp-wrap" style={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto' }}>
        <table className="cmp rz cmp-sticky" style={{ width: rz.totalWidth(colIds) }}>
          <colgroup>
            {colIds.map((id) => <col key={id} style={{ width: rz.wOf(id) }} />)}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2}>序号{rz.handle('seq')}</th>
              <th rowSpan={2}>项目名称 / Item{rz.handle('name')}</th>
              <th rowSpan={2}>项目特征 / Spec{rz.handle('spec')}</th>
              <th rowSpan={2}>单位{rz.handle('unit')}</th>
              <th rowSpan={2}>工程量{rz.handle('qty')}</th>
              <th rowSpan={2} className="ref-col">参考单价 / Ref{rz.handle('ref')}</th>
              {viewCols.map((c) => {
                const bid = c.bid;
                const cp = bid ? (CLEAN_PILL[bid.cleaning_status] ?? CLEAN_PILL.pending) : CLEAN_PILL.pending;
                return (
                  <th key={c.key} colSpan={2} className={colClass(c)}>
                    <div style={{ whiteSpace: 'normal' }} title={c.label}>{c.label}
                      {!inRoundMode && c.e.award_status === 'awarded' && <span className="win-pill">✓ 中标</span>}
                      {!inRoundMode && awarded && c.e.award_status === 'lost' && <span className="lost-pill">未选</span>}
                    </div>
                    <div style={{ margin: '3px 0' }}>
                      {viewMode === 'byLabor' && (
                        <select value={roundFor(c.e)} onChange={(ev) => setRounds((r) => ({ ...r, [c.e.id]: Number(ev.target.value) }))} style={{ width: 'auto', padding: '2px 6px', fontSize: 12 }}>
                          {activeBids(c.e).map((b) => <option key={b.id} value={b.round_number}>R{b.round_number}</option>)}
                        </select>
                      )}
                      {bid && <span className={`pill ${cp.cls} clean-pill`} style={{ marginLeft: viewMode === 'byLabor' ? 6 : 0 }}>{cp.zh}</span>}
                    </div>
                    {/* F5 · lock state on the labor's column: byLabor = the labor's single column; other modes = the live final-offer round's column */}
                    {(viewMode === 'byLabor' || bid?.id === c.e.final_offer_bid_id) && finalPill(c.e) && <div style={{ margin: '2px 0' }}>{finalPill(c.e)}</div>}
                  </th>
                );
              })}
            </tr>
            <tr>
              {viewCols.map((c) => (
                <Fragment key={c.key}>
                  <th className={`num ${colClass(c)}`} style={{ fontSize: 11.5 }}>不含税单价{rz.handle(`up_${c.key}`)}</th>
                  <th className={`num ${colClass(c)}`} style={{ fontSize: 11.5 }}>不含税合计{rz.handle(`amt_${c.key}`)}</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {pkg.work_items.map((w, i) => {
              // this line's high/low across the shown columns (drives the relative color marking)
              const ups = viewCols.map((c) => lineOf(c.bid, w)?.unit_price_rmb).filter((p): p is number => p != null);
              const hi = ups.length >= 2 ? Math.max(...ups) : null;
              const lo = ups.length >= 2 && Math.min(...ups) !== Math.max(...ups) ? Math.min(...ups) : null;
              return (
                <tr key={w.id}>
                  {/* fb_6685b243 (改改): 对比与定标 numbers items by the uploaded 清单's own 序号 */}
                  <td className="num">{w.seq_label ?? i + 1}</td>
                  <td title={w.item_label}>{w.item_label}</td>
                  <td className="small muted" title={w.scope_description_md ?? undefined}>{w.scope_description_md ?? '—'}</td>
                  <td>{w.unit ?? '—'}</td>
                  <td className="num">{w.qty ?? '—'}</td>
                  <td className="ref-col num">{rmb(w.ref_unit_price_rmb)}</td>
                  {viewCols.map((c) => bidderCells(c, w, hi, lo))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>合计（含自行补充）/ Total (incl. add-ons)</td>
              {/* F3 · a partial 参考价 sum must not read as a whole-package total next to the labor totals */}
              <td className="ref-col num">{rmb(sumRef(pkg) || null)}{(() => { const n = pkg.work_items.filter((w) => w.ref_unit_price_rmb != null).length; return n > 0 && n < pkg.work_items.length ? <div className="small muted">已设 {n}/{pkg.work_items.length} 项</div> : null; })()}</td>
              {viewCols.map((c) => {
                const total = c.bid?.total_rmb ?? null;
                const pct = total != null && envelope && !(c.bid && isArchived(c.bid)) ? Math.round(((total - envelope) / envelope) * 1000) / 10 : null;
                const hi = pct != null && pct > 15;
                // 改改 #3 (方案 B): the 合计 INCLUDES each bidder's 自行补充项 — surface the 清单/补充 split so the
                // headline number is no longer ambiguous about what's folded in.
                // a TRUE supplement has neither a work_item link nor a snapshot name (archived real lines
                // have work_item_id nulled by the FK but carry snapshot_item_label — they are 清单 lines).
                const supp = (c.bid?.line_items ?? []).filter((l) => l.work_item_id == null && l.snapshot_item_label == null).reduce((s, l) => s + (l.amount_rmb ?? 0), 0);
                const listTotal = total != null ? Math.round((total - supp) * 100) / 100 : null;
                return (
                  <Fragment key={c.key}>
                    <td className={colClass(c)}></td>
                    <td className={`num ${colClass(c)}`}>
                      <div className={hi ? 'total-hi' : 'total-ok'}>{rmb(total)}</div>
                      {supp > 0 && <div className="small muted">清单 {rmb(listTotal)} ＋ 补充 {rmb(supp)}</div>}
                      {pct != null && <div className="small muted">{pct > 0 ? '+' : ''}{pct}% vs 测算价</div>}
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* F3 · 自行补充项 per bidder (改改): free-form lines the bidder added (no work_item), for the selected round */}
      {cols.some((e) => (bidFor(e)?.line_items ?? []).some((l) => l.work_item_id == null && l.item_label)) && (
        <div className="card" style={{ marginTop: 12 }}>
          <strong style={{ fontSize: 13 }}>自行补充项 / Self-added items（劳务方补充·清单未列）</strong>
          <div className="row-wrap" style={{ marginTop: 8, gap: 16 }}>
            {cols.map((e) => {
              const supps = (bidFor(e)?.line_items ?? []).filter((l) => l.work_item_id == null && l.item_label);
              if (!supps.length) return null;
              return (
                <div key={e.id} style={{ flex: '1 1 240px' }}>
                  <div className="small" style={{ fontWeight: 600 }}>{e.labor_co_name}</div>
                  <table className="cmp" style={{ fontSize: 12, marginTop: 4 }}>
                    <tbody>
                      {supps.map((l) => (
                        <tr key={l.id}>
                          <td>{l.item_label}{l.qty_quoted != null && <span className="muted"> ×{l.qty_quoted}</span>}</td>
                          <td className="num">{rmb(l.amount_rmb)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="row-wrap" style={{ marginTop: 12 }}>
        <span className="small muted">测算价 / envelope {rmb(envelope)} · 容差上限 +15% = {rmb(envelope ? envelope * 1.15 : null)} · 颜色：<span className="out-high">红=本行最高单价</span> / <span className="out-low">绿=本行最低单价</span>（{inRoundMode ? '各轮相对 / across rounds' : inAllMode ? '各家各轮相对 / across labors & rounds' : '各家相对 / across labors'}）</span>
      </div>

      <div className="row-wrap" style={{ marginTop: 10 }}>
        {cols.map((e) => (
          <div key={e.id} className="card" style={{ flex: '1 1 240px', margin: 0, padding: '10px 12px' }}>
            <strong>{e.labor_co_name}</strong> {finalPill(e)}
            <div className="row" style={{ marginTop: 8, flexWrap: 'wrap' }}>
              <button className={e.shortlisted ? 'btn-warn' : ''} onClick={() => shortlist.mutate({ eid: e.id, on: !e.shortlisted })} disabled={shortlist.isPending}>
                {e.shortlisted ? '🌟 已入围' : '入围 / Shortlist'}
              </button>
              {/* F4 · refetch the package when the panel opens so external changes (e.g. labor confirmed) show */}
              {!awarded && <button onClick={() => { invalidate(); setNegEng(e); }}>💬 议价 / Negotiate</button>}
              {/* Staff "view as labor" (7/3): open a READ-ONLY preview of this supplier's portal — see what
                  they see (deadline, thread, final-offer card) without being able to act as them. */}
              {e.labor_co_id && <button title="以该劳务的视角查看门户（只读）/ read-only preview of their portal" onClick={async () => {
                try { await startPortalPreview(e.labor_co_id!); window.location.href = '/portal'; }
                catch (err: any) { alert(String(err?.body?.error ?? err?.message ?? err)); }
              }}>👁 劳务视角 / View as</button>}
              {!awarded && <button className="btn-good"
                onClick={() => { if (confirm(`定标 ${e.labor_co_name}？/ Award to ${e.labor_co_name}?`)) award.mutate(e.id); }}
                disabled={award.isPending || voteBlocked}
                title={voteBlocked ? '定标投票未完成，投票齐了才能定标 / voting incomplete — all votes must be in before awarding' : undefined}
              >🏆 定标 / Award{voteBlocked ? ' · 投票未完成' : ''}</button>}
              {e.award_status === 'awarded' && <button className="btn-danger" onClick={() => unaward.mutate()} disabled={unaward.isPending}>撤销定标 / Un-award</button>}
              <CreateTaskHere
                context={{ kind: 'bid_row', label: e.labor_co_name ?? '—', package_id: pkg.id, engagement_id: e.id, view_tab: 'compare' }}
                packageId={pkg.id}
                pageUrl={`/packages/${pkg.id}?tab=compare`}
                prefillTitle={`复核报价：${e.labor_co_name ?? ''}`}
                compact
              />
            </div>
          </div>
        ))}
      </div>
      {(shortlist.isError || award.isError) && <div className="small" style={{ color: 'var(--bad)', marginTop: 8 }}>{String((shortlist.error || award.error as any)?.message)}</div>}

      <CompanyHistoryPanel pid={pkg.id} />

      {/* F4 · derive the panel's engagement from the LIVE package query — the captured negEng object goes
          stale the moment an action (final-offer / withdraw / labor confirm) invalidates the package, which
          made the panel show ⏳待确认 while its own thread said 已确认 (7/2 walkthrough). */}
      {negEng && <NegotiationPanel pkg={pkg} eng={pkg.engagements.find((x) => x.id === negEng.id) ?? negEng} onClose={() => setNegEng(null)} />}
    </div>
  );
}

// 参考价 total = Σ(参考单价 × 工程量) over items that have a reference price (mirrors WorkItemsPanel).
function sumRef(pkg: PackageDetail): number {
  return pkg.work_items.reduce((s, w) => s + (w.ref_unit_price_rmb != null && w.qty != null ? w.ref_unit_price_rmb * w.qty : 0), 0);
}
